/**
 * /validation/runs/new — open a release validation run.
 *
 * The run starts from an existing **release risk assessment** (AI → Release
 * risk assessment). Attaching one extracts its findings as risk items the lead
 * confirms in the next step. The Veeva connection is optional: without it
 * every test is manual; with it, the test author may mark tests automatic and
 * the platform executes them against the vault.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { IconArrowLeft, IconInfoCircle, IconRocket } from "@tabler/icons-react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import { listAssessments, type AssessmentOut } from "@/shared/api/ai";
import { createRun } from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

const PRODUCTS = [
  "RIM (Regulatory)",
  "Claims (PromoMats)",
  "Quality",
  "Clinical (CTMS)",
  "CDMS",
  "Safety",
  "Commercial",
];

interface Deployment {
  name: string;
  label?: string;
  model?: string;
}

function deploymentsOf(connection: ConnectionOut | undefined): Deployment[] {
  const raw = ((connection?.config ?? {}) as Record<string, unknown>).deployments;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (d): d is Deployment =>
      typeof d === "object" && d !== null && typeof (d as Deployment).name === "string",
  );
}

export function NewRun() {
  const getToken = useAccessToken();
  const navigate = useNavigate();

  const [ai, setAi] = useState<ConnectionOut[] | null>(null);
  const [veeva, setVeeva] = useState<ConnectionOut[] | null>(null);
  const [blob, setBlob] = useState<ConnectionOut[] | null>(null);
  const [assessments, setAssessments] = useState<AssessmentOut[] | null>(null);

  const [title, setTitle] = useState("");
  const [product, setProduct] = useState<string | null>("RIM (Regulatory)");
  const [changeRef, setChangeRef] = useState("");
  const [description, setDescription] = useState("");
  const [aiId, setAiId] = useState<string | null>(null);
  const [deployment, setDeployment] = useState<string | null>(null);
  const [veevaId, setVeevaId] = useState<string | null>(null);
  const [blobId, setBlobId] = useState<string | null>(null);
  const [assessmentId, setAssessmentId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const [a, v, b, past] = await Promise.all([
        listConnections(token, "ai_foundry"),
        listConnections(token, "veeva"),
        listConnections(token, "blob"),
        listAssessments(token),
      ]);
      setAi(a.items);
      setVeeva(v.items);
      setBlob(b.items);
      const ok = past.items.filter((x) => x.status === "succeeded");
      setAssessments(ok);
      setAiId(a.items[0]?.connection_id ?? null);
      setDeployment(deploymentsOf(a.items[0])[0]?.name ?? null);
      setVeevaId(v.items[0]?.connection_id ?? null);
      setBlobId(b.items[0]?.connection_id ?? null);
      if (ok[0]) {
        setAssessmentId(ok[0].assessment_id);
        if (!title) setTitle(ok[0].title);
      }
    } catch (err) {
      toastApiError("Could not load connections", err);
      setAi([]);
      setVeeva([]);
      setBlob([]);
      setAssessments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- title default only on first load
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const deployments = deploymentsOf(ai?.find((c) => c.connection_id === aiId));
  const ready = Boolean(title.trim() && product && aiId && deployment && !submitting);

  const onSubmit = async () => {
    if (!ready || !product || !aiId || !deployment) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      const run = await createRun(token, {
        title: title.trim(),
        vault_product: product,
        change_ref: changeRef.trim() || null,
        description: description.trim() || null,
        ai_connection_id: aiId,
        deployment,
        veeva_connection_id: veevaId,
        blob_connection_id: blobId,
        risk_assessment_id: assessmentId,
      });
      toastSuccess(
        "Run created",
        assessmentId
          ? `${run.risks?.length ?? 0} risk items extracted — confirm them next.`
          : "Attach a risk assessment to begin.",
      );
      navigate(`/validation/runs/${run.run_id}`);
    } catch (err) {
      toastApiError("Could not create the run", err);
    } finally {
      setSubmitting(false);
    }
  };

  const loading = ai === null || assessments === null;

  return (
    <Stack gap="md" maw={860}>
      <Group gap="xs">
        <Button
          component={RouterLink}
          to="/validation/runs"
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={14} />}
        >
          Runs
        </Button>
      </Group>
      <div>
        <Title order={2}>New validation run</Title>
        <Text c="dimmed" size="sm">
          One release, one run. Start from the risk assessment; the plan and tests follow.
        </Text>
      </div>

      {loading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <>
          {ai.length === 0 && (
            <Alert variant="light" color="yellow" icon={<IconInfoCircle size={18} />}>
              This needs an Azure AI Foundry connection. Add one under Integrations.
            </Alert>
          )}
          {assessments.length === 0 && (
            <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
              No completed risk assessment yet.{" "}
              <Anchor component={RouterLink} to="/ai/risk-assessment" size="sm">
                Run one first
              </Anchor>{" "}
              — it becomes stage 1 of the run — or create the run now and attach it later.
            </Alert>
          )}

          <Paper withBorder radius="md" p="lg">
            <Stack gap="md">
              <Text fw={600}>Release</Text>
              <TextInput
                label="Title"
                placeholder="CHG-0042 — Deviation void state"
                value={title}
                onChange={(e) => setTitle(e.currentTarget.value)}
                required
              />
              <Group grow align="flex-start">
                <Select
                  label="Vault product"
                  data={PRODUCTS}
                  value={product}
                  onChange={setProduct}
                  allowDeselect={false}
                />
                <TextInput
                  label="Change reference"
                  description="ServiceNow CHG, RfC id, or the Veeva release name"
                  placeholder="CHG-0042"
                  value={changeRef}
                  onChange={(e) => setChangeRef(e.currentTarget.value)}
                />
              </Group>
              <Textarea
                label="Description"
                placeholder="Business context of the change — what it does and why."
                autosize
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="lg">
            <Stack gap="md">
              <Text fw={600}>Stage 1 — risk assessment</Text>
              <Select
                label="Release risk assessment"
                description="A completed comparison of the current and upcoming configuration. Its findings become the risk items you confirm."
                placeholder="Attach later"
                data={assessments.map((a) => ({
                  value: a.assessment_id,
                  label: `${a.title} · ${new Date(a.created_at_utc).toLocaleDateString()}`,
                }))}
                value={assessmentId}
                onChange={setAssessmentId}
                clearable
                searchable
              />
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="lg">
            <Stack gap="md">
              <Text fw={600}>Connections</Text>
              <Group grow align="flex-start">
                <Select
                  label="AI connection"
                  data={ai.map((c) => ({ value: c.connection_id, label: c.display_name }))}
                  value={aiId}
                  onChange={(next) => {
                    setAiId(next);
                    setDeployment(
                      deploymentsOf(ai.find((c) => c.connection_id === next))[0]?.name ?? null,
                    );
                  }}
                  allowDeselect={false}
                />
                <Select
                  label="Model"
                  data={deployments.map((d) => ({
                    value: d.name,
                    label: d.label ?? d.model ?? d.name,
                  }))}
                  value={deployment}
                  onChange={setDeployment}
                  allowDeselect={false}
                  disabled={deployments.length === 0}
                />
              </Group>
              <Group grow align="flex-start">
                <Select
                  label="Veeva Vault (for automatic tests)"
                  description="Optional. Without it every test is manual."
                  data={(veeva ?? []).map((c) => ({
                    value: c.connection_id,
                    label: c.display_name,
                  }))}
                  value={veevaId}
                  onChange={setVeevaId}
                  clearable
                />
                <Select
                  label="Blob (for exports)"
                  description="Optional. Where the plan and report PDFs can be saved."
                  data={(blob ?? []).map((c) => ({
                    value: c.connection_id,
                    label: c.display_name,
                  }))}
                  value={blobId}
                  onChange={setBlobId}
                  clearable
                />
              </Group>
            </Stack>
          </Paper>

          <Group justify="flex-end">
            <Button
              leftSection={<IconRocket size={16} />}
              onClick={() => void onSubmit()}
              loading={submitting}
              disabled={!ready}
            >
              {assessmentId ? "Create run and extract risks" : "Create run"}
            </Button>
          </Group>
          {submitting && assessmentId && (
            <Text size="xs" c="dimmed" ta="right">
              Reading the assessment and extracting risk items — a few seconds.
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}
